import moment from 'moment';

import { DateFormat } from '../../utils/date';
import { MONEY_DECIMALS, formatAmoutInCents, formatCurrencyCents } from '../../utils/formatters';
import { DEFAULT_CURRENCY } from '../../utils/currency';
import { DURATION_UNIT, toMomentUnit } from '../../utils/durationUnit';
import { getUpfrontPayment } from './challengePricing';

import { generateTerms } from './userContractTerms';

// This is the default version we have for new contracts.
const CONTRACT_VERSION = 2;

const ContractProperties = {
  START_DATE: 'startDate',
  INITIAL_PAYMENT: 'initialPayment',
  MONTHLY_PAYMENT: 'monthlyPayment',
  INITIAL_TERM: 'initialTerm',
  MIN_SUBSCRIPTION_MONTHS: 'minSubscriptionMonths',
  PRICE_ID: 'priceId',
  MONTHLY_PRICE_ID: 'monthlyPriceId',
  CURRENCY: 'currency',
  CANCEL_AT_PERIOD_END: 'cancelAtPeriodEnd',
  RECURRING_BILLING_MONTHS: 'recurringBillingMonths',
  PACKAGE_ID: 'packageId',
  PACKAGE_NAME: 'packageName',
  VERSION: 'version',
  TYPE: 'type',
  NAME: 'name',
  SELLER_ID: 'sellerId',
  BASE_FEE: 'baseFee',
  AC_FEE: 'assistantCoachFee',
  IS_FEE: 'insideSalesFee',
  CHALLENGE_FEE: 'challengeFee',
  AI_FRIEND_FEE: 'aiFriendFee',
  CHALLENGE_ID: 'challengeId',
  ACCEPT_UNTIL: 'acceptUntil',
  UPFRONT_PAYMENT: 'upfrontPayment',
  POST_COMMITMENT_PAYMENT: 'postCommitmentPayment',
  UPSELL_PRODUCT_ID: 'upsellProductId',
  UPSELL_MONTHLY_PRICE: 'upsellMonthlyPrice',
  CHECKOUT_EXPERIENCE: 'checkoutExperience',
  DURATION_UNIT: 'durationUnit',
  SKIP_TERMS: 'skipTerms',
  IS_STORY_SHARE_ENABLED: 'isStoryShareEnabled',
};

const CheckoutExperience = {
  STRIPE_HOSTED: 'stripe',
  AVIDA_MANAGED: 'managed',
};

/*
  One definition per shape, unit-agnostic, so the form (strings) and the UserContract model (cents)
  cannot drift from each other or from isStandardMonthlyRecurringContract in the server's
  hostedCheckoutEligibility.ts — where they disagree, the coach configures a contract the server then
  refuses at checkout.

  Number() rather than a typeof check because the form passes strings: it also keeps a legacy document
  missing both amounts from reading as standard monthly, since NaN === NaN is false. The model adds its
  own typeof guard on top, matching the server's.
*/
const isStandardMonthlyByAmounts = ({
  initialTerm, firstPaymentAmount, monthlyAmount, upfrontAmount, postCommitmentAmount,
}) => Number(initialTerm) === 1
  && Number(firstPaymentAmount) === Number(monthlyAmount)
  && !Number(upfrontAmount)
  && !Number(postCommitmentAmount);

const isStandardMonthlyRecurringContract = (contract) => isStandardMonthlyByAmounts({
  initialTerm: contract.initialTerm,
  firstPaymentAmount: contract.initialPayment,
  monthlyAmount: contract.monthlyPayment,
  upfrontAmount: contract.upfrontPayment,
  postCommitmentAmount: contract.postCommitmentPayment,
});

/*
  A contract's start date is a bare calendar day ('YYYY-MM-DD') with no time or timezone: it means
  the day the coach picked on their calendar, not an instant. The value arrives in two shapes — a
  stored contract holds the string, while the configurator's form holds whatever its date picker
  produced (a Dayjs, since that picker is mounted with AdapterDayjs rather than the AdapterMoment
  used elsewhere). Both libraries implement format(), so both reduce to the same calendar day that
  gets persisted; anything else is treated as absent rather than reaching the strict parse, which
  would read an unparseable value as "not future".
*/
const toCalendarDay = (startDate) => {
  if (!startDate) {
    return null;
  }
  const startDay = typeof startDate === 'string'
    ? startDate
    : startDate.format?.(DateFormat.DEFAULT_DATE_FORMAT);
  return typeof startDay === 'string' ? startDay : null;
};

/*
  Authoring check: is the day the coach picked still ahead of the day they are living in?

  Compared on the LOCAL day, because that is the basis every other start-date rule in the contract
  modal uses — `isDateValid` gates saving on `moment().startOf('day')`, and the future-start billing
  section reads `contract.startDate.isAfter(moment())`. Comparing in UTC here would disagree with
  those for any coach east of UTC during their morning: `isDateValid` rejects anything before their
  local today while a UTC comparison calls their local today "future", leaving no date that is both
  saveable and eligible, and the selector silently disappearing for the first hours of their day.

  The runtime counterpart below is deliberately more permissive, so a contract the coach was allowed
  to configure here can never be refused at checkout.
*/
const isFutureStartDate = (startDate) => {
  const startDay = toCalendarDay(startDate);
  if (startDay === null) {
    return false;
  }
  const parsedStartDay = moment(startDay, DateFormat.DEFAULT_DATE_FORMAT, true);
  return parsedStartDay.isValid() && parsedStartDay.isAfter(moment().startOf('day'));
};

/*
  Runtime check: has the start day plausibly arrived anywhere yet?

  Used where the coach's timezone is not available — the customer's browser at checkout — and
  mirrored by the server, which has no timezone context at all. Both compare against start-of-day
  UTC plus one day rather than the coach's local day, which they cannot see.

  The extra day is what makes the two checks safe together: no timezone runs more than one day ahead
  of UTC, so `local-today <= utc-today + 1` always holds. This can therefore never be stricter than
  the authoring check above — a contract the coach was allowed to set to Stripe Hosted can never be
  refused here or by the server. A refusal is recoverable: the onboarding page falls back to the
  Elements card when the hosted-checkout call fails. But it is not free — a wasted round-trip, a
  Sentry error for every affected customer, and the coach's chosen experience silently not applied
  — and that fallback falls out of a loading flag rather than being designed, so it is not
  something to lean on. It stays strict where it matters: a contract starting next week is still
  beyond tomorrow and still correctly future-dated.
*/
const isFutureStartDateAtCheckout = (startDate) => {
  const startDay = toCalendarDay(startDate);
  if (startDay === null) {
    return false;
  }
  const parsedStartDay = moment.utc(startDay, DateFormat.DEFAULT_DATE_FORMAT, true);
  return parsedStartDay.isValid() && parsedStartDay.isAfter(moment.utc().add(1, 'day').startOf('day'));
};

const isDiscountedFirstMonthByAmounts = ({
  initialTerm, firstPaymentAmount, monthlyAmount, upfrontAmount, postCommitmentAmount,
}) => Number(initialTerm) === 1
  && Number(firstPaymentAmount) > 0
  && Number(firstPaymentAmount) < Number(monthlyAmount)
  && !Number(upfrontAmount)
  && !Number(postCommitmentAmount);

const isDiscountedFirstMonthContract = (contract) => isDiscountedFirstMonthByAmounts({
  initialTerm: contract.initialTerm,
  firstPaymentAmount: contract.initialPayment,
  monthlyAmount: contract.monthlyPayment,
  upfrontAmount: contract.upfrontPayment,
  postCommitmentAmount: contract.postCommitmentPayment,
});

const contractHasFutureStartDate = (contract) => isFutureStartDate(contract?.startDate);

// Discounted-first-month contracts are only checkout-eligible when they start immediately: the first-month
// discount is delivered as a one-time coupon on the first invoice, which the future-start flow does not support.
const isDiscountedFirstMonthCheckoutEligible = (contract) => isDiscountedFirstMonthContract(contract)
  && !contractHasFutureStartDate(contract);

const isStandardMonthlyCheckoutEligible = (contract) => isStandardMonthlyRecurringContract(contract)
  && !contractHasFutureStartDate(contract);

const isCheckoutExperienceEligibleProduct = (product) => !!product
  && !product.isChallengeProduct
  && !product.isAiFriendEnabled
  && !product.isHyroxProduct;

const isCheckoutExperienceEligibleContract = (contract, product) => isCheckoutExperienceEligibleProduct(product)
  && (isStandardMonthlyCheckoutEligible(contract) || isDiscountedFirstMonthCheckoutEligible(contract));

/*
  What a saved contract stores as its checkout experience. A challenge is sold on Stripe Hosted Checkout unless
  Avida-managed is picked for it, so it stores Stripe Hosted by default. Every other product is Avida-managed unless
  it is eligible for, and set to, Stripe Hosted.
*/
const getStoredCheckoutExperience = ({ checkoutExperience, isCheckoutExperienceEligible, product }) => {
  if (product?.isChallengeProduct) {
    return checkoutExperience || CheckoutExperience.STRIPE_HOSTED;
  }
  return (isCheckoutExperienceEligible && checkoutExperience) || CheckoutExperience.AVIDA_MANAGED;
};

const FeeType = {
  PERCENTAGE: 'PERCENTAGE',
  FIXED: 'FIXED',
};

const ContractType = {
  BASE: 'BASE',
  FROM_BASE: 'FROM_BASE',
  USER: 'USER',
  ONE_TIME: 'ONE_TIME',
};

const FeeConfig = {
  INSIDE_SALES: {
    value: 15,
    type: FeeType.PERCENTAGE,
  },
  ASSISTANT_COACH: {
    value: 15,
    type: FeeType.PERCENTAGE,
  },
};

const ContractStatus = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  ON_BREAK: 'ON_BREAK',
  DELINQUENT: 'DELINQUENT',
  CANCELED: 'CANCELED',
  PAST_DUE: 'PAST_DUE',
  INCOMPLETE: 'INCOMPLETE',
  FUTURE_CANCELLATION: 'FUTURE_CANCELLATION',
  TRIALING: 'TRIALING',
  IN_PROGRESS: 'IN_PROGRESS',
  FAILED: 'FAILED',
  INCOMPLETE_EXPIRED: 'INCOMPLETE_EXPIRED',
  ACCEPTED: 'ACCEPTED',
  AMENDMENT_ACCEPTED: 'AMENDMENT_ACCEPTED',
  RETENTION_PENDING_ACTIVATION: 'RETENTION_PENDING_ACTIVATION',
};

/* Contracts that are pending or in progress */
const FUTURE_PENDING_CONTRACT_STATUSES = [
  ContractStatus.PENDING,
  ContractStatus.IN_PROGRESS,
];

/* Contracts that are already accepted for a future period */
const FUTURE_ACCEPTED_CONTRACT_STATUSES = [
  ContractStatus.ACCEPTED,
  ContractStatus.AMENDMENT_ACCEPTED,
  ContractStatus.RETENTION_PENDING_ACTIVATION,
];

const FUTURE_CONTRACT_STATUSES = [
  ...FUTURE_PENDING_CONTRACT_STATUSES,
  ...FUTURE_ACCEPTED_CONTRACT_STATUSES,
];

const ContractMinimumValue = {
  MIN_SUBSCRIPTION_MONTHS: 1,
  INITIAL_TERM: 1,
  FEE_PERCENTAGE: 0,
};

const ContractMaxValue = {
  /*
    Upper bound on the contract term, per duration unit. The same real-world
    length needs a bigger count in weeks than in months, so the ceiling differs
    by unit. Only challenge contracts can carry a non-month unit.
  */
  INITIAL_TERM_BY_UNIT: {
    [DURATION_UNIT.MONTH]: 12,
    [DURATION_UNIT.WEEK]: 52,
  },
  FEE_PERCENTAGE: 100,
  AI_FRIEND_FEE_PERCENTAGE: 60,
  CHALLENGE_FEE_PERCENTAGE: 60,
};

/**
 * Upper bound on the contract term for a given duration unit. An absent or
 * unknown unit falls back to the month cap, matching the stored-value default.
 *
 * @param {'week'|'month'} [durationUnit] The contract's `durationUnit`.
 * @returns {number} The maximum term count allowed in that unit.
 */
const getMaxInitialTerm = (durationUnit) => (
  ContractMaxValue.INITIAL_TERM_BY_UNIT[durationUnit]
  ?? ContractMaxValue.INITIAL_TERM_BY_UNIT[DURATION_UNIT.MONTH]
);

/**
 * The term ceiling actually enforced on a contract, which never falls below a
 * value the contract already carries.
 *
 * The per-unit cap used to be only the input's `max` attribute, never a
 * save-time gate, so contracts above it exist. Enforcing the bare cap would
 * make those documents unamendable — the coach could not save any edit,
 * including ones that have nothing to do with the term. Raising the ceiling to
 * the stored value keeps the cap meaningful for anything a coach types while
 * leaving existing contracts editable.
 *
 * @param {'week'|'month'} [durationUnit] The contract's `durationUnit`.
 * @param {number} [storedInitialTerm] The term on the loaded contract document, if any.
 * @returns {number} The maximum term count this contract may be saved with.
 */
const getAllowedMaxInitialTerm = (durationUnit, storedInitialTerm) => Math.max(
  getMaxInitialTerm(durationUnit),
  Number(storedInitialTerm) || 0,
);

const DEFAULT_CHALLENGE_AI_FRIEND_FEE_PERCENTAGE = 30;

/**
 * Derives a single fee object from an existing contract's fees.
 * Used when updating a contract and switching between product types.
 * Checks: target fee → sibling single-fee (challenge ↔ AI friend) → sum of regular fees → default.
 * @param {object} prevContract - Contract object with fee properties
 * @param {string} feeProperty - Target fee key (e.g. CHALLENGE_FEE, AI_FRIEND_FEE)
 * @returns {object} Object with one key (feeProperty) and the derived fee value
 */
const getSingleFeeFromExisting = (prevContract, feeProperty) => {
  const existingFee = prevContract[feeProperty];
  if (existingFee !== undefined) {
    return { [feeProperty]: existingFee };
  }

  // Check sibling single-fee type (challenge ↔ AI friend conversion)
  const siblingProperty = feeProperty === ContractProperties.CHALLENGE_FEE
    ? ContractProperties.AI_FRIEND_FEE
    : ContractProperties.CHALLENGE_FEE;
  const siblingFee = prevContract[siblingProperty];
  if (siblingFee !== undefined) {
    return { [feeProperty]: siblingFee };
  }

  const sum = (prevContract[ContractProperties.BASE_FEE] || 0)
    + (prevContract[ContractProperties.AC_FEE] || 0)
    + (prevContract[ContractProperties.IS_FEE] || 0);
  const fee = sum || DEFAULT_CHALLENGE_AI_FRIEND_FEE_PERCENTAGE;
  return { [feeProperty]: Math.min(fee, ContractMaxValue.AI_FRIEND_FEE_PERCENTAGE) };
};

/**
 * Extracts the original base/AC/IS fee values from a UserContract document.
 * Used to restore fees when switching away from a single-fee contract type (challenge/AI Friend).
 * @param {object} contractDoc - UserContract document instance
 * @returns {object} Object with ContractProperties fee keys present in the original contract
 */
const getOriginalFeesFromContractDoc = (contractDoc) => {
  const { baseFee: bF, assistantCoachFee: acF, insideSalesFee: isF } = contractDoc.fees;
  return {
    ...(bF?.value !== undefined ? { [ContractProperties.BASE_FEE]: bF.value } : {}),
    ...(acF?.value !== undefined ? { [ContractProperties.AC_FEE]: acF.value } : {}),
    ...(isF?.value !== undefined ? { [ContractProperties.IS_FEE]: isF.value } : {}),
  };
};

const DefaultContract = {
  [ContractProperties.START_DATE]: moment(),
  [ContractProperties.INITIAL_PAYMENT]: '',
  [ContractProperties.MONTHLY_PAYMENT]: '',
  [ContractProperties.POST_COMMITMENT_PAYMENT]: '',
  [ContractProperties.PRICE_ID]: '',
  [ContractProperties.MONTHLY_PRICE_ID]: '',
  [ContractProperties.INITIAL_TERM]: ContractMinimumValue.INITIAL_TERM,
  [ContractProperties.MIN_SUBSCRIPTION_MONTHS]: '',
  [ContractProperties.CURRENCY]: DEFAULT_CURRENCY.code,
  [ContractProperties.CANCEL_AT_PERIOD_END]: false,
  [ContractProperties.RECURRING_BILLING_MONTHS]: 1,
  [ContractProperties.PACKAGE_ID]: '',
  [ContractProperties.PACKAGE_NAME]: '',
  [ContractProperties.VERSION]: CONTRACT_VERSION,
  [ContractProperties.NAME]: '',
  [ContractProperties.UPFRONT_PAYMENT]: 0,
  [ContractProperties.UPSELL_PRODUCT_ID]: '',
  [ContractProperties.UPSELL_MONTHLY_PRICE]: '',
  [ContractProperties.DURATION_UNIT]: DURATION_UNIT.MONTH,
  [ContractProperties.SKIP_TERMS]: false,
  [ContractProperties.IS_STORY_SHARE_ENABLED]: false,
};

/*
  Whether every payment on the (form-shaped) contract is an explicit zero.
  parseFloat keeps an untouched '' from counting as free: an admin has to type
  the zeros. Gates both the admin-only $0 validation and the skipTerms flag.
*/
const isContractPricingFree = ({ monthlyPayment, initialPayment, upfrontPayment }) => (
  Number.parseFloat(monthlyPayment) === 0
  && Number.parseFloat(initialPayment) === 0
  && (Number.parseFloat(upfrontPayment) || 0) === 0
);

const getContractObj = (contractData) => {
  const {
    startDate: stringDate,
    initialPaymentInCents,
    totalPriceInCents,
    initialTerm,
    minSubscriptionMonths,
    currency,
    priceId = '',
    monthlyPriceId = '',
    cancelAtPeriodEnd,
    recurringBillingMonths,
    coachConditions,
    packageId = '',
    packageName = '',
    upfrontPaymentInCents = '',
    version = CONTRACT_VERSION,
    postCommitmentPriceInCents,
    challengeId = null,
    upsellProductId = '',
    upsellMonthlyPriceInCents,
    checkoutExperience = CheckoutExperience.AVIDA_MANAGED,
    /*
      Absent on every contract created before week durations existed, and on
      every non-challenge contract. Defaulting here (rather than leaving it
      undefined) means the loaded contract object always carries a unit, so
      editing a stored week challenge can't silently downgrade it to months.
    */
    durationUnit = DURATION_UNIT.MONTH,
    skipTerms = false,
    isStoryShareEnabled = false,
  } = contractData;

  return {
    startDate: moment(stringDate),
    initialPayment: formatAmoutInCents(initialPaymentInCents, MONEY_DECIMALS),
    monthlyPayment: formatAmoutInCents(totalPriceInCents, MONEY_DECIMALS),
    upfrontPayment: formatAmoutInCents(upfrontPaymentInCents, MONEY_DECIMALS),
    ...(postCommitmentPriceInCents
      && { postCommitmentPayment: formatAmoutInCents(postCommitmentPriceInCents, MONEY_DECIMALS) }),
    initialTerm,
    minSubscriptionMonths,
    currency,
    priceId,
    monthlyPriceId,
    cancelAtPeriodEnd,
    recurringBillingMonths,
    coachConditions,
    packageId,
    packageName,
    version,
    checkoutExperience,
    durationUnit,
    skipTerms,
    isStoryShareEnabled,
    ...(challengeId && { challengeId }),
    ...(upsellProductId && { upsellProductId }),
    ...(upsellMonthlyPriceInCents && {
      upsellMonthlyPrice: formatAmoutInCents(upsellMonthlyPriceInCents, MONEY_DECIMALS),
    }),
  };
};

const getContractDocContent = ({
  lead,
  user,
  contract,
  product,
  coachDoc,
  sellerId,
  isAmendment = false,
  originalContract = '',
}) => {
  // Get the coach data
  const {
    id: coach,
    name: coachName,
    termsAndConditionsText,
  } = coachDoc;

  // Get the lead data
  const {
    id: leadId,
    name = '',
    firstName: leadFirstName = '',
    lastName: leadLastName = '',
    email: leadEmail = '',
    phoneNumber: leadPhoneNumber = '',
    country = '',
  } = lead || {};

  const {
    firstName: userDocFirstName,
    lastName: userDocLastName,
    email: userDocEmail,
    id: userId,
  } = user || {};

  const userFirstName = userDocFirstName || leadFirstName;
  const userLastName = userDocLastName || leadLastName;
  const userEmail = userDocEmail || leadEmail;
  const phoneNumber = leadPhoneNumber;

  const userNameString = userFirstName ? `${userFirstName} ${userLastName}` : name;
  const userName = userNameString.trim();

  // Get the product data
  const {
    id: productId,
    name: productName,
  } = product;

  const isAiFriendContract = !!product?.isAiFriendEnabled;

  // Get the contract-specific data
  const {
    startDate: startDateValue,
    initialPayment,
    monthlyPayment,
    initialTerm,
    minSubscriptionMonths,
    currency,
    priceId = '',
    monthlyPriceId = '',
    cancelAtPeriodEnd,
    recurringBillingMonths,
    packageId = '',
    packageName = '',
    version,
    baseFee,
    assistantCoachFee,
    insideSalesFee,
    challengeFee,
    aiFriendFee,
    name: contractName,
    sellerId: contractSellerId,
    postCommitmentPayment,
    upsellProductId,
    upsellMonthlyPrice,
    checkoutExperience,
    durationUnit = DURATION_UNIT.MONTH,
    skipTerms = false,
    isStoryShareEnabled = false,
    challengeId = null,
  } = contract;

  const isCheckoutExperienceEligible = isCheckoutExperienceEligibleContract(contract, product);

  // Get the contract terms so that we can save them in the contract document.
  const terms = generateTerms(contract, isAmendment);

  // Build the price object
  const price = {
    priceId,
    monthlyPriceId,
    /*
      A challenge is billed once, so the amount due at checkout is what must be stored here — the
      monthly field is hidden on challenge forms and seeded with a placeholder. The form handlers pin
      the two together, but this is the single line that turns the form value into the charged amount,
      so the rule is enforced here rather than trusted to every path that can reach it.
    */
    totalPriceInCents: Math.round(parseFloat(challengeId ? initialPayment : monthlyPayment) * 100),
    currency,
    minSubscriptionMonths,
    initialTerm,
    initialPaymentInCents: Math.round(parseFloat(initialPayment) * 100),
    cancelAtPeriodEnd,
    recurringBillingMonths,
    // A challenge is billed once at checkout, so it carries no advance-plus-balance split.
    upfrontPaymentInCents: Math.round(parseFloat(getUpfrontPayment(contract)) * 100),
    /*
      The unit that `initialTerm` and `minSubscriptionMonths` are counted in.
      Always written so the value round-trips through an edit; consumers that
      predate it treat an absent unit as months, which is what this defaults to.
    */
    durationUnit,
    ...(postCommitmentPayment && { postCommitmentPriceInCents: Math.round(parseFloat(postCommitmentPayment) * 100) }),
  };

  // Build the fees object
  const fees = isAiFriendContract
    ? {
      ...(aiFriendFee ? {
        aiFriendFee: {
          value: aiFriendFee,
          type: FeeType.PERCENTAGE,
        },
      } : {}),
    }
    : {
      ...(baseFee ? {
        baseFee: {
          value: baseFee,
          type: FeeType.PERCENTAGE,
        },
      } : {}),
      ...(insideSalesFee ? {
        insideSalesFee: {
          value: insideSalesFee,
          type: FeeType.PERCENTAGE,
        },
      } : {}),
      ...(assistantCoachFee ? {
        assistantCoachFee: {
          value: assistantCoachFee,
          type: FeeType.PERCENTAGE,
        },
      } : {}),
      ...(challengeFee ? {
        challengeFee: {
          value: challengeFee,
          type: FeeType.PERCENTAGE,
        },
      } : {}),
    };

  // build the upsell object
  const upsellContract = upsellProductId && upsellMonthlyPrice
    ? {
      productId: upsellProductId,
      monthlyPriceInCents: Math.round(parseFloat(upsellMonthlyPrice) * 100),
    }
    : {};

  const startDate = startDateValue.clone().format(DateFormat.DEFAULT_DATE_FORMAT);

  // Use sellerId specified in the contract object, use the one passed as param otherwise.
  const sellerIdValue = contractSellerId || sellerId;

  const docContent = {
    coach,
    coachName,
    sellerId: sellerIdValue,
    leadId: leadId || '',
    userFirstName,
    userLastName,
    userName,
    userEmail: userEmail || '',
    userId: userId || '',
    phoneNumber,
    productId,
    price,
    fees,
    startDate,
    status: ContractStatus.PENDING,
    coachConditions: termsAndConditionsText,
    country,
    packageId,
    packageName,
    version,
    terms,
    name: contractName,
    productName,
    /*
      Only a fully free contract may skip the terms step: the onboarding page
      guards on isFree too, but a paid contract must never even store the flag.
    */
    skipTerms: !!skipTerms && isContractPricingFree(contract),
    // Only a challenge offers the commitment sticker after checkout, whatever the form held before the product changed.
    isStoryShareEnabled: !!isStoryShareEnabled && !!product.isChallengeProduct,
    isAmendment,
    /*
      The contract this amendment replaces. The server resolves which subscription to
      schedule for cancellation through this link — a client may hold several, and an
      amendment without it can only be accepted while the client's subscription is
      unambiguous.
    */
    ...(isAmendment && originalContract && { originalContract }),
    upsellContract,
    checkoutExperience: getStoredCheckoutExperience({ checkoutExperience, isCheckoutExperienceEligible, product }),
  };

  return docContent;
};

const getBaseContractDocContent = (contract, product, coachDoc, sellerDoc) => {
  let contractDocContent = getContractDocContent({}, contract, product, coachDoc, sellerDoc.id);

  // Add the specific properties for base contracts.
  contractDocContent = {
    ...contractDocContent,
    type: ContractType.BASE,
    status: ContractStatus.ACTIVE,
  };

  return contractDocContent;
};

const getRemainingPaymentCount = (contract, user, subscription) => {
  const minSubscriptionMonths = contract?.minSubscriptionMonths
    || ContractMinimumValue.MIN_SUBSCRIPTION_MONTHS;
  const initialTerm = contract?.initialTerm || ContractMinimumValue.INITIAL_TERM;
  const commitmentEndDate = user.serviceStartAt
    ? moment(user.serviceStartAt).add(minSubscriptionMonths, 'months')
    : moment.unix(subscription.created).add(minSubscriptionMonths, 'months');
  const remainingMonths = commitmentEndDate.diff(moment(), 'months');

  if (remainingMonths > 0) {
    // check if the user is still in initial term, if so return the remaining months excluding the initial term
    if (remainingMonths > minSubscriptionMonths - initialTerm) {
      return minSubscriptionMonths - initialTerm;
    }
    return remainingMonths;
  }
  return 0;
};

// Weeks billed as a month when normalizing a week term to a monthly figure.
const WEEKS_PER_MONTH = 4;

/**
 * Get the average MONTHLY payment for the initial term of a contract.
 *
 * Always monthly, whatever unit the term is counted in — this figure feeds
 * revenue calculations, so a week challenge is converted to its monthly
 * equivalent rather than reported per week. A 6-week challenge at 75 averages
 * (75 / 6) * 4 = 50 per month; a 3-week one at 75 averages 100.
 *
 * @param {object} paymentData
 * @param {number} paymentData.initialPayment - Initial payment (in units of currency, not cents. For example: 75).
 * @param {number} paymentData.initialTerm - Initial term, counted in `durationUnit`.
 * @param {'week'|'month'} [paymentData.durationUnit] - Unit the term is counted in. Absent means months.
 * @returns {number} Average monthly payment in cents.
 */
const getAverageMonthlyPayment = (paymentData = {}) => {
  const {
    initialPayment,
    initialTerm = 1,
    durationUnit,
  } = paymentData;

  // Ensure we have the correct types before doing the calculations
  const initialPaymentValue = parseFloat(initialPayment || 0) * 100;
  const initialTermValue = parseInt(initialTerm, 10);

  const averagePerUnit = initialPaymentValue / initialTermValue;

  return durationUnit === DURATION_UNIT.WEEK ? averagePerUnit * WEEKS_PER_MONTH : averagePerUnit;
};

/**
 * Get a formatted average monthly payment for the initial term of a contract.
 * @param {object} paymentData
 * @param {number} paymentData.initialPayment - Initial payment (in units of currency, not cents. For example, 75).
 * @param {number} paymentData.initialTerm - Initial term, counted in `durationUnit`.
 * @param {string} paymentData.currency - Currency code.
 * @returns {string} Formatted average monthly payment.
 */
const getFormattedAverageMonthlyPayment = (paymentData = {}) => {
  const averageMonthlyPayment = getAverageMonthlyPayment(paymentData);
  return formatCurrencyCents(averageMonthlyPayment, paymentData.currency, MONEY_DECIMALS);
};

const getUserSubscriptionPrice = (contract, plan) => {
  if (contract) {
    return formatCurrencyCents(contract.totalPriceInCents, contract.currency);
  }
  if (plan) {
    return formatCurrencyCents(plan.totalPriceInCents, plan.currency);
  }
  return '';
};

/**
 * Number of days a challenge of the given initial term spans, measured from its start date.
 *
 * @param {number} initialTerm - Challenge initial term, counted in `durationUnit`.
 * @param {'week'|'month'} [durationUnit] - Unit the term is counted in. Absent means months.
 * @param {object} startDate - Date the challenge starts (moment or date-like).
 * @returns {number} Required number of program day-slots.
 */
const getChallengeDurationDays = (initialTerm = 0, durationUnit, startDate) => {
  const start = moment(startDate?.toDate?.() ?? startDate).startOf('day');
  return start.clone().add(initialTerm, toMomentUnit(durationUnit)).diff(start, 'days');
};

/**
 * Whether the selected program contains enough day-slots to cover the full challenge period.
 * @param {number} programWorkoutDays - Length of the program's `workouts` array.
 * @param {number} initialTerm - Challenge initial term, counted in `durationUnit`.
 * @param {'week'|'month'} [durationUnit] - Unit the term is counted in. Absent means months.
 * @param {object} [startDate] - Date the challenge starts, see getChallengeDurationDays.
 * @returns {boolean}
 */
const isProgramLongEnoughForChallenge = (programWorkoutDays = 0, initialTerm = 0, durationUnit, startDate) => (
  programWorkoutDays >= getChallengeDurationDays(initialTerm, durationUnit, startDate)
);

export {
  ContractProperties,
  toCalendarDay,
  CheckoutExperience,
  isStandardMonthlyRecurringContract,
  isStandardMonthlyCheckoutEligible,
  contractHasFutureStartDate,
  isDiscountedFirstMonthByAmounts,
  isStandardMonthlyByAmounts,
  isDiscountedFirstMonthCheckoutEligible,
  isCheckoutExperienceEligibleProduct,
  isFutureStartDate,
  isFutureStartDateAtCheckout,
  ContractMinimumValue,
  ContractMaxValue,
  getMaxInitialTerm,
  getAllowedMaxInitialTerm,
  DEFAULT_CHALLENGE_AI_FRIEND_FEE_PERCENTAGE,
  getSingleFeeFromExisting,
  getOriginalFeesFromContractDoc,
  ContractStatus,
  ContractType,
  FeeType,
  FeeConfig,
  DefaultContract,
  FUTURE_PENDING_CONTRACT_STATUSES,
  FUTURE_ACCEPTED_CONTRACT_STATUSES,
  FUTURE_CONTRACT_STATUSES,
  getContractObj,
  getContractDocContent,
  getBaseContractDocContent,
  isContractPricingFree,
  getRemainingPaymentCount,
  getAverageMonthlyPayment,
  getFormattedAverageMonthlyPayment,
  getUserSubscriptionPrice,
  getChallengeDurationDays,
  isProgramLongEnoughForChallenge,
};
